import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { MarketplaceItem, ProductCard } from "@itaas/pep-contracts";
import { getMarketplaceItem, getProducts } from "../api/pep-api";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";
import { labelIndustry, productDisplayName } from "../utils/labels";
import {
  formatPricingModel,
  labelMarketplaceStatus,
  labelMarketplaceType,
  marketplaceDescription,
  marketplaceDisplayName,
  marketplaceStatusBadge,
} from "../utils/marketplace-labels";

export function MarketplaceDetailPage() {
  const { slug = "" } = useParams();
  const [item, setItem] = useState<MarketplaceItem | null>(null);
  const [products, setProducts] = useState<ProductCard[]>([]);

  useEffect(() => {
    if (!slug) return;
    Promise.all([getMarketplaceItem(slug), getProducts()])
      .then(([mp, prods]) => {
        setItem(mp);
        setProducts(prods);
      })
      .catch(console.error);
  }, [slug]);

  if (!item) {
    return (
      <SiteLayout>
        <div className="container py-5 text-secondary">Chargement…</div>
      </SiteLayout>
    );
  }

  const name = marketplaceDisplayName(item);
  const desc = item.longDescriptionFr ?? marketplaceDescription(item);
  const compatible = products.filter((p) => item.productSlugs.includes(p.slug));

  return (
    <SiteLayout>
      <SeoHead
        title={name}
        description={desc.slice(0, 160)}
        keywords={item.seoKeywords}
        canonicalPath={`/marketplace/${item.slug}`}
      />
      <div className="pep-page-hero py-4 mb-0">
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb pep-breadcrumb mb-3">
              <li className="breadcrumb-item">
                <Link to="/marketplace">Marketplace</Link>
              </li>
              <li className="breadcrumb-item active">{name}</li>
            </ol>
          </nav>
          <div className="d-flex flex-wrap gap-2 mb-2">
            <span className="badge text-bg-light border">{labelMarketplaceType(item.type)}</span>
            <span className={`badge ${marketplaceStatusBadge(item.status)}`}>
              {labelMarketplaceStatus(item.status)}
            </span>
            {item.publisher === "itaas" && <span className="badge text-bg-dark">ITaaS</span>}
            {item.publisher === "partner" && (
              <span className="badge text-bg-info">
                Partenaire
                {item.partnerSlug && (
                  <>
                    {" "}
                    —{" "}
                    <Link className="text-white" to={`/partners#${item.partnerSlug}`}>
                      voir
                    </Link>
                  </>
                )}
              </span>
            )}
          </div>
          <h1 className="display-6 fw-bold mb-2">{name}</h1>
          {item.taglineFr && <p className="lead mb-0 opacity-90">{item.taglineFr}</p>}
        </div>
      </div>

      <div className="container py-5">
        <div className="row g-5">
          <div className="col-lg-8">
            <p className="text-secondary">{desc}</p>

            {item.features && item.features.length > 0 && (
              <>
                <h2 className="h5 fw-bold mt-4 mb-3">Fonctionnalités</h2>
                <ul className="text-secondary">
                  {item.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </>
            )}

            {item.industries && item.industries.length > 0 && (
              <>
                <h2 className="h5 fw-bold mt-4 mb-3">Secteurs</h2>
                <div className="d-flex flex-wrap gap-2">
                  {item.industries.map((ind) => (
                    <span key={ind} className="badge text-bg-light border">
                      {labelIndustry(ind)}
                    </span>
                  ))}
                </div>
              </>
            )}

            <h2 className="h5 fw-bold mt-4 mb-3">Produits compatibles</h2>
            <div className="row g-3">
              {compatible.map((p) => (
                <div className="col-sm-6" key={p.slug}>
                  <Link
                    className="card border-0 shadow-sm text-decoration-none h-100 pep-marketplace-compat"
                    to={`/products/${p.slug}`}
                  >
                    <div className="card-body p-3">
                      <div className="fw-semibold">{productDisplayName(p)}</div>
                      <div className="small text-secondary">Voir le produit →</div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card border-0 shadow-sm sticky-top pep-sidebar-card" style={{ top: "5rem" }}>
              <div className="card-body p-4">
                <div className="h4 fw-bold text-primary mb-3">
                  {formatPricingModel(item.pricingModel, item.monthlyUsd)}
                </div>
                {item.version && (
                  <p className="small text-secondary mb-3">Version {item.version}</p>
                )}

                {item.status === "coming_soon" ? (
                  <button type="button" className="btn btn-secondary w-100 mb-2" disabled>
                    Bientôt disponible
                  </button>
                ) : (
                  <Link className="btn btn-primary w-100 mb-2" to="/configure">
                    Demander l&apos;activation
                  </Link>
                )}
                <Link className="btn btn-outline-primary w-100" to="/configure">
                  Créer mon environnement
                </Link>

                {item.pricingModel === "subscription" && (
                  <p className="small text-secondary mt-3 mb-0">
                    Tarif indicatif — validation commerciale requise.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}
